// Read layer for the Back Sections (Front Page & Back Sections, FP-3 — web).
//
// Read contract, verified against iOS `FirestoreService.fetchSectionSet`
// (FirestoreService.swift:222) and firestore.rules:409:
//
//   sectionSets/{play-day} → { sections: { [category]: [qid, qid, qid] } }
//   questions/{id}         → the same shape the Daily Six uses, EXCEPT
//                            status:"sectionActive" and lane:"section"
//   users/{uid}/answers/{id} → the user's prior pick (merged for resume)
//
// Both collections are auth-readable by rule, so this mirrors iOS's direct
// read — there is no section-fetch callable and web doesn't need one. What web
// DOES need from the server is the §3f visibility answer, because iOS resolves
// it as `RemoteConfig.sections_enabled || sectionsFounder` and web has no
// Remote Config: see `fetchSectionsGate` below.
//
// D1 SHIELD, and why nothing here filters on it: section questions carry
// status:"sectionActive" precisely so they stay invisible to every
// `status == "active"` pool query (old iOS binaries included). We arrive via
// the sectionSets INDEX, not a pool query, so the shield is already satisfied —
// re-filtering on status here would only risk dropping a legitimately-published
// question if the constant ever changes on one side.
//
// The play-day key is `playDayString()` (3am ET roll), identical to dailySet.js.

import { db, fn } from '../firebase.js';
import { playDayString } from '../util/etDate.js';
import { getScreenCache, setScreenCache } from '../util/screenCache.js';

/**
 * Can this user see sections at all? (§3f.)
 *
 * Asks the server rather than deriving locally. The client CAN read its own
 * `sectionsFounder` — but that is only half the OR, and would silently stay
 * false for everyone the moment Tom flips `publicEnabled`, which is
 * admin-only-readable. `getSectionsGate` returns `sectionsVisibleFor`, the one
 * server truth every other section surface already calls.
 *
 * Fails CLOSED: any error → false. Sections are still dark for the public, so
 * a transient failure showing nothing is strictly better than showing a
 * surface that isn't supposed to exist yet.
 */
export async function fetchSectionsGate() {
  try {
    const r = await fn.getSectionsGate({});
    const visible = r?.data?.visible === true;
    // Cached so surfaces that merely need to KNOW the answer (the I CALLED IT
    // share's arena, 2026-08-05) don't each spend a callable on it. The cache
    // is play-day-stamped, so a flip of `publicEnabled` reaches every screen
    // by the next 3 AM ET roll at the latest, and sooner on any Play visit.
    setScreenCache(SECTIONS_GATE_KEY, visible);
    return visible;
  } catch {
    return false;
  }
}

const SECTIONS_GATE_KEY = 'sectionsGate';

/** The last known gate answer, or false if this session hasn't asked yet.
 *  Fails closed exactly as `fetchSectionsGate` does. */
export function cachedSectionsGate() {
  return getScreenCache(SECTIONS_GATE_KEY) === true;
}

/** The gate answer, asking the server only if nothing is cached for this play
 *  day. For surfaces that need a REAL answer (the challenge-scope chooser)
 *  rather than the fail-closed read above, without spending a callable on
 *  every mount. */
export async function ensureSectionsGate() {
  const cached = getScreenCache(SECTIONS_GATE_KEY);
  if (typeof cached === 'boolean') return cached;
  return fetchSectionsGate();
}

/** Shape one section question doc into the object the ballot + write-contract
 *  expect. Deliberately the SAME shape `dailySet.js` decodeQuestion produces —
 *  `submitDailySet` writes section answers through the identical path, so any
 *  divergence here would produce a subtly different answer doc. */
function decodeSectionQuestion(id, data) {
  if (!data) return null;
  return {
    id,
    text: data.text ?? '',
    category: data.category ?? 'Markets',
    imageURL: typeof data.imageURL === 'string' ? data.imageURL : null,
    publishDate: data.publishDate?.toDate?.() ?? new Date(),
    estimatedResolutionDate: data.estimatedResolutionDate?.toDate?.() ?? new Date(),
    communityYesPercent:
      typeof data.communityYesPercent === 'number' ? data.communityYesPercent : 50,
    communityNoPercent:
      typeof data.communityNoPercent === 'number' ? data.communityNoPercent : 50,
    correctAnswer: typeof data.correctAnswer === 'boolean' ? data.correctAnswer : null,
    isResolved: data.isResolved === true,
    isVoided: data.isVoided === true,
    revealHeadline: data.revealHeadline ?? null,
    // Dig Deeper context (web parity, 2026-07-30) — sections carry blurbs
    // too; the context button gates on the blurb exactly like the six.
    contextBlurb: typeof data.contextBlurb === 'string' && data.contextBlurb ? data.contextBlurb : null,
    contextURL: typeof data.contextURL === 'string' && data.contextURL ? data.contextURL : null,
    contextSourceName: typeof data.contextSourceName === 'string' && data.contextSourceName ? data.contextSourceName : null,
    contextSourceDate: typeof data.contextSourceDate === 'string' && data.contextSourceDate ? data.contextSourceDate : null,
    // MORE ON THIS STORY (news-first N4.3): shared slug linking a drilled
    // section question to its category's six question. Absent = no ribbon.
    storyTag: typeof data.storyTag === 'string' && data.storyTag ? data.storyTag : null,
    askedByCreator: null,
    // The lane discriminator, carried through so the UI can mark these as
    // section questions and never offer a 2x on one (FP-2 D4: section
    // double-downs are expressive-only, so offering the chip would be a lie).
    lane: 'section',
    answer: null,
    isSkipped: false,
    isDoubleDown: false,
    answered: false,
  };
}

/**
 * Fetch ONE category's back section for a play-day, with the caller's prior
 * answers merged. Returns an array (empty = no section published for that
 * category today, which is a valid state, not an error). Throws only on a hard
 * read failure so the caller can show a retry.
 *
 * Per-document gets rather than an `in` query: it's the established app-web
 * idiom (see fetchTodaySetMerged), it sidesteps the `in` cap entirely, and it
 * needs no composite index.
 */
export async function fetchSectionForCategory(category, uid, playDay = playDayString()) {
  if (!category) return [];
  const setSnap = await db.collection('sectionSets').doc(playDay).get();
  if (!setSnap.exists) return [];

  const sections = setSnap.data()?.sections;
  const ids = Array.isArray(sections?.[category]) ? sections[category] : [];
  if (ids.length === 0) return [];

  // One wave: question docs + this user's answer docs, in parallel.
  const [qSnaps, aSnaps] = await Promise.all([
    Promise.all(ids.map((id) => db.collection('questions').doc(id).get())),
    uid
      ? Promise.all(
          ids.map((id) =>
            db.collection('users').doc(uid).collection('answers').doc(id).get()
          )
        )
      : Promise.resolve([]),
  ]);

  return ids
    .map((id, i) => {
      const qSnap = qSnaps[i];
      const q =
        qSnap && qSnap.exists ? decodeSectionQuestion(qSnap.id, qSnap.data()) : null;
      if (!q) return null;
      const aSnap = aSnaps[i];
      if (aSnap && aSnap.exists) {
        const a = aSnap.data();
        q.answer = typeof a.answer === 'boolean' ? a.answer : null;
        q.isSkipped = a.isSkipped === true;
        q.answered = typeof a.answer === 'boolean' || a.isSkipped === true;
      }
      return q;
    })
    .filter(Boolean);
}

/**
 * Which categories have a section published today. Powers the post-six
 * "more questions" routing, so a category the user follows but which has no
 * section today is never offered as an empty room.
 */
export async function fetchAvailableSectionCategories(playDay = playDayString()) {
  const setSnap = await db.collection('sectionSets').doc(playDay).get();
  if (!setSnap.exists) return [];
  const sections = setSnap.data()?.sections;
  if (!sections || typeof sections !== 'object') return [];
  return Object.keys(sections).filter(
    (c) => Array.isArray(sections[c]) && sections[c].length > 0
  );
}
